<template>
  <div>
    <Head title="Work Order - woodwork consumptions" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-orange-400 hover:text-orange-600" href="/work-orders/woodwork/consumptions">
        Work Orders
      </Link>
      <span class="text-orange-400 font-medium">/</span>{{ workOrder.WO_number }}/Consumption
    </h1>
    <div 
      v-show="workOrder.status == 1"
      class="max-w-full bg-white rounded-md shadow mb-3"
    >
      <form @submit.prevent="store">
        <div class="pb-2 pr-6 w-full lg:w-full mb-8">
          <div
            v-for="(offcut, index) in form.offcuts"
            :key="index"
            class="flex flex-wrap -mb-16 -mr-6 p-8"
          >
            <div class="pb-2 pr-6 w-full lg:w-7/12">
              <multiselect
                v-model="offcut.sku_id"
                id="sku_id"
                placeholder="Search SKU"
                :options="skus"
                :close-on-select="true"
                :searchable="true"
                :error="showValidationErrors(`offcuts.${index}.sku_id`)"
                :style= "showValidationErrors(`offcuts.${index}.sku_id`) ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                @input="onSelectedSku"
              />
              <div v-if="showValidationErrors(`offcuts.${index}.sku_id`)" class="form-error">{{ showValidationErrors(`offcuts.${index}.sku_id`) }}</div>
            </div>
            <div class="pb-2 pr-6 w-full lg:w-3/12">
              <text-input
                :key="index"
                class="w-full lg:w-full"
                v-model="offcut.quantity"
                :error="showValidationErrors(`offcuts.${index}.quantity`)"
                placeholder="Quantity"
              />
            </div>
            <div class="pb-2 pr-6 w-full lg:w-1/12">
              <div class="w-full lg:w-1/2">
                <svg
                  v-show="form.offcuts.length > 1"
                  @click="removeOffcut(index, form.offcuts)"
                  xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="w-full lg:w-1/2">
                <svg
                  v-show="index === countOffcuts"
                  @click="addOffcut"
                  xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100"
        >
          <loading-button :loading="form.processing" class="btn-orange" type="submit">
            Add
          </loading-button>
        </div>
      </form>
    </div>
    <hr/>
    <ul class="nav nav-tabs flex flex-col md:flex-row flex-wrap list-none border-b-0 pl-0 mb-4" id="tabs-tabFill"
      role="tablist">
      <li class="nav-item flex-auto text-center" role="presentation">
        <a href="#tabs-profileFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
          active
        " id="tabs-profile-tabFill" data-bs-toggle="pill" data-bs-target="#tabs-profileFill" role="tab"
          aria-controls="tabs-profileFill" aria-selected="false">Scans</a>
      </li>
      <li class="nav-item flex-auto text-center" role="presentation">
        <a href="#tabs-summaryFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
        " id="tabs-summary-tabFill" data-bs-toggle="pill" data-bs-target="#tabs-summaryFill" role="tab"
          aria-controls="tabs-summaryFill" aria-selected="false">Summaries</a>
      </li>
    </ul>
    <div class="tab-content" id="tabs-tabContentFill">
      <div class="tab-pane fade show active" id="tabs-profileFill" role="tabpanel" aria-labelledby="tabs-profile-tabFill">
        <div class="flex items-center justify-between mb-6">
          <a
            v-if="woodworks.data.length > 0"
            class="btn-orange px-3 py-2 text-xs cursor-pointer"
            :href="`/work-orders/${workOrder.id}/woodwork/consumptions/scans/download`"
            download
            target="__blank"
          >
            <span>Download</span>
          </a>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Date</th>
              <th class="pb-3 pt-3 px-3">WO Number</th>
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Part Description</th>
              <th class="pb-3 pt-3 px-3 text-center">Quantity</th>
              <th class="pb-3 pt-3 px-3">UoM</th>
              <th class="pb-3 pt-3 px-3">Created By</th>
            </tr>
            <tr v-for="woodwork in woodworks.data" :key="woodwork.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <span class="flex items-center px-3 py-2 focus:text-orange-500">
                  {{ woodwork.date }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2" tabindex="-1">
                  {{ woodwork.wo_number }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2" tabindex="-1">
                  {{ woodwork.part_no }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2" tabindex="-1">
                  {{ woodwork.description }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ woodwork.quantity }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ woodwork.measure }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2" tabindex="-1">
                  {{ woodwork.user }}
                </span>
              </td>
            </tr>
            <tr v-if="woodworks.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="8">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="woodworks.links" />
      </div>
      <div class="tab-pane fade" id="tabs-summaryFill" role="tabpanel" aria-labelledby="tabs-summary-tabFill">
        <div class="flex items-center justify-between mb-6">
          <a
            v-if="issuace_summaries.data.length > 0"
            class="btn-orange px-3 py-2 text-xs cursor-pointer"
            :href="`/work-orders/${workOrder.id}/woodwork/consumptions/summaries/download`"
            download
            target="__blank"
          >
            <span>Download</span>
          </a>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Part Description</th>
              <th class="pb-3 pt-3 px-3">Quantity</th>
              <th class="pb-3 pt-3 px-3">UoM</th>
            </tr>
            <tr
              v-for="inventory in issuace_summaries.data"
              :key="inventory.id"
              class="hover:bg-orange-100 focus-within:bg-gray-100"
            >
              <td class="border-t">
                <span class="flex items-center px-3 py-2 focus:text-indigo-500">
                  {{ inventory.sku_name }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2 focus:text-indigo-500">
                  {{ inventory.description }}
                </span>
              </td>
              <td class="border-t">
                <span class="flex items-center px-3 py-2 focus:text-indigo-500">
                  {{ inventory.quantity }}
                </span>
              </td>
              <td class="w-px border-t">
                <span class="flex items-center px-3 py-2 focus:text-indigo-500 text-right">
                  {{ inventory.unit_of_measure }}
                </span>
              </td>
            </tr>
            <tr v-if="issuace_summaries.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="8">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="issuace_summaries.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Pagination from '@/Shared/Pagination'
import DateRange from '@/Shared/DateRange'
import Multiselect from '@vueform/multiselect' 

export default {
  components: {
    Head,
    Icon,
    Link,
    DateRange,
    TextInput,
    SelectInput,
    LoadingButton,
    Pagination,
    Multiselect,
  },
  layout: Layout,
  remember: 'form',
  props: {
    filters: Object,
    workOrder: Object,
    woodworks: Object,
    issuace_summaries: Object,
    skus: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        offcuts: [{
          sku_id: null,
          quantity: null,
        }],
      }),
      search: {
        dateRange: this.filters.dateRange
      },
      summary: {
        dateRange: this.filters.dateRange
      },
      selectedSku: null,
    }
  },
  computed: {
    countOffcuts() {
      if (this.form.offcuts.length === 0) {
        return 0;
      }
      return this.form.offcuts.length - 1;
    },
    role() {
      return this.$inertia.page.props.auth.user.role
    },
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/work-orders/${this.workOrder.id}/woodwork/consumptions/create`, pickBy(this.search), { preserveState: true })
      }, 150),
    },
    summary: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/work-orders/${this.workOrder.id}/woodwork/consumptions/create`, pickBy(this.summary), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    resets() {
      this.summary = mapValues(this.summary, () => null)
    },
    addOffcut(e) {
      e.preventDefault();
      this.form.offcuts.push({
        sku_id: null,
        quantity: null,
      });
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return !!error;
      }
      return false;
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || '';
        return error || '';
      }
      return '';
    },
    removeOffcut(index, offcuts) {
      offcuts.splice(index, 1)
    },
    store() {
      this.form.post(`/work-orders/${this.workOrder.id}/woodwork/consumptions/store`, {
        onSuccess: () => {
          this.form.reset(
            'offcuts',
            'sku_id',
            'quantity',
          )
        }
      })
    },
    onSearchSkusChange: throttle(function (search) {
        this.$inertia.get(`/work-orders/${this.workOrder.id}/woodwork/consumptions/create`, {search}, {
          preserveState: true,
          preserveScroll: true,
          replace: true
        })
      }, 200),
    onSelectedSku(sku, id) {
      this.form.sku_id = sku;
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>